import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { JSX } from 'react'; 

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  selectedSettingsType: string;
  selectedSettingsSubType: string;
  selectedOldKeywordName: string;
  selectedOldHeader: string;
  selectedOldValue: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onSave,
  selectedSettingsType,
  selectedSettingsSubType,
  selectedOldKeywordName,
  selectedOldHeader,
  selectedOldValue
}) => {
  const [settingsType, setSettingsType] = useState<string>(selectedSettingsType);
  const [settingsSubType, setSettingsSubType] = useState<string>(selectedSettingsSubType);
  
  const [oldKeywordName, setOldKeyword] = useState<string>(selectedOldKeywordName);
  const [oldHeader, setOldHeader] = useState<string>(selectedOldHeader);
  const [oldValue, setOldValue] = useState<string>(selectedOldValue);

  const [keywordName, setKeyword] = useState<string>(
    ["edit", "remove"].includes(selectedSettingsSubType) ? selectedOldKeywordName : ""
  );
  
  const [header, setHeader] = useState<string>(
    ["edit", "remove"].includes(selectedSettingsSubType) ? selectedOldHeader : ""
  );
  
  const [value, setValue] = useState<string>(
    ["edit", "remove"].includes(selectedSettingsSubType) ? selectedOldValue : ""
  );

  const handleSave = () => {
    if (settingsSubType === 'remove') {
      onSave(keywordName); // Call the parent function with the input value
      onClose(); // Close modal after saving
      return;
    }

    const data = {
      keywordName,
      oldKeywordName,
      header: settingsType === 'keyword_list' ? null : header,
      value: settingsType === 'keyword_list' ? null : value,
      oldHeader,
      oldValue
    };

    onSave(data); // Call the parent function with the input value
    setKeyword(""); // Clear input after saving
    setHeader("");
    setValue("");
    onClose(); // Close modal after saving
  };

  const renderInputFields = (): JSX.Element => {
    if (settingsSubType === 'remove') {
      return (
        <div className="famous-clicker-save-rule-confirmation">
          <p>Are you sure you want to delete this rule?</p>
        </div>
      );
    }

    return (
      <>
        {settingsType === 'keyword_list' && (
          <>
            <div className="famous-clicker-save-rule-input-group">
              <input
                type="text"
                className="famous-clicker-save-rule-input-field"
                placeholder=" "
                id="ruleName"
                value={keywordName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
              />
              <label className="famous-clicker-save-rule-input-label" htmlFor="ruleName">
                Keyword
              </label>
            </div>
          </>
        )}
        
        {settingsType === 'template_list' && (
          <>
            <div className="famous-clicker-save-rule-input-group">
              <textarea
                className="famous-clicker-save-rule-input-field"
                rows={5}
                placeholder=" "
                id="value"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
              />
              <label className="famous-clicker-save-rule-input-label" htmlFor="value">
                Template
              </label>
            </div>
          </>
        )}
        {settingsType === 'text_list' && (
          <>
            <div className="famous-clicker-save-rule-input-group">
              <input
                type="text"
                className="famous-clicker-save-rule-input-field"
                placeholder=" "
                id="header"
                value={header}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeader(e.target.value)}
              />
              <label className="famous-clicker-save-rule-input-label" htmlFor="header">
                Name
              </label>
            </div>
            <div className="famous-clicker-save-rule-input-group">
              <textarea
                className="famous-clicker-save-rule-input-field"
                rows={5}
                placeholder=" "
                id="value"
                value={value}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
              />
              <label className="famous-clicker-save-rule-input-label" htmlFor="value">
                Template
              </label>
            </div>
          </>
        )}
      </>
    );
  };

  const modalContent = (
    <div
      className={`famous-clicker-save-rule-modal-backdrop ${
        isOpen ? "famous-clicker-save-rule-show" : ""
      }`}
      onClick={onClose}
    >
      <div
        className="famous-clicker-save-rule-modal-container"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <div className="famous-clicker-save-rule-modal-header">
          <h2 className="famous-clicker-save-rule-modal-title">
            {selectedSettingsSubType + ' ' + {
              toggle: "General",
              keyword_list: "Keywords",
              template_list: "Templates",
              text_list: "Text Lists"
            }[settingsType] || settingsType}
          </h2>
        </div>
        {renderInputFields()}
        <div className="famous-clicker-save-rule-button-group">
          <button className="famous-clicker-save-rule-btn famous-clicker-save-rule-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="famous-clicker-save-rule-btn famous-clicker-save-rule-btn-primary"
            onClick={handleSave}
          >
            {settingsSubType === 'Delete' ? 'Confirm Delete' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;